import { useEffect, useState } from 'react';
import API_URL from '../config/api';
import {
  buildFreshUrl,
  CONTENT_SYNC_KEYS,
  subscribeToContentUpdates,
} from '../utils/contentSync';
import { useSiteContent } from '../context/SiteContentContext';
import {
  getSessionCache,
  removeSessionCache,
  setSessionCache,
} from '../utils/sessionCache';
import { buildSiteSearchDocuments } from '../utils/siteSearch';

const SEARCH_CACHE_TTL_MS = 5 * 60 * 1000;
const PRODUCTS_CACHE_KEY = 'site-search-products';
const NEWS_CACHE_KEY = 'site-search-news';
const CERTIFICATES_CACHE_KEY = 'site-search-certificates';
const CATALOGS_CACHE_KEY = 'site-search-catalogs';

function getCachedCollection(key) {
  const cachedValue = getSessionCache(key, SEARCH_CACHE_TTL_MS);

  if (Array.isArray(cachedValue)) {
    return {
      hasCache: true,
      value: cachedValue,
    };
  }

  return {
    hasCache: false,
    value: [],
  };
}

async function fetchCollection(url, signal) {
  const response = await fetch(url, {
    signal,
    headers: {
      Accept: 'application/json',
    },
  });
  const data = await response.json().catch(() => null);

  if (!response.ok) {
    throw new Error(data?.message || data?.error || `Request failed: ${response.status}`);
  }

  return data;
}

export function useSiteSearch(options = {}) {
  const { enabled = true } = options;
  const cachedProducts = getCachedCollection(PRODUCTS_CACHE_KEY);
  const cachedNews = getCachedCollection(NEWS_CACHE_KEY);
  const cachedCertificates = getCachedCollection(CERTIFICATES_CACHE_KEY);
  const cachedCatalogs = getCachedCollection(CATALOGS_CACHE_KEY);
  const { siteContent, isSiteContentLoading } = useSiteContent();

  const [products, setProducts] = useState(cachedProducts.value);
  const [newsItems, setNewsItems] = useState(cachedNews.value);
  const [certificates, setCertificates] = useState(cachedCertificates.value);
  const [catalogs, setCatalogs] = useState(cachedCatalogs.value);
  const [refreshToken, setRefreshToken] = useState(0);
  const [isSearchDataLoading, setIsSearchDataLoading] = useState(
    enabled &&
      (!cachedProducts.hasCache ||
        !cachedNews.hasCache ||
        !cachedCertificates.hasCache ||
        !cachedCatalogs.hasCache)
  );

  useEffect(() => {
    if (!enabled) {
      setIsSearchDataLoading(false);
      return undefined;
    }

    const controller = new AbortController();
    let cancelled = false;
    const pendingRequests = [];
    const needsFetch =
      !cachedProducts.hasCache ||
      !cachedNews.hasCache ||
      !cachedCertificates.hasCache ||
      !cachedCatalogs.hasCache;

    setIsSearchDataLoading(needsFetch);

    if (!cachedProducts.hasCache) {
      pendingRequests.push(
        fetchCollection(buildFreshUrl(API_URL.PRODUCTS), controller.signal)
          .then((data) => {
            const nextProducts = Array.isArray(data?.products)
              ? data.products
              : Array.isArray(data?.data)
                ? data.data
                : [];

            if (!cancelled) {
              setProducts(nextProducts);
              setSessionCache(PRODUCTS_CACHE_KEY, nextProducts);
            }
          })
          .catch((error) => {
            if (error.name !== 'AbortError') {
              console.error('Failed to build product search index:', error);
            }
          })
      );
    }

    if (!cachedNews.hasCache) {
      pendingRequests.push(
        fetchCollection(buildFreshUrl(API_URL.NEWS), controller.signal)
          .then((data) => {
            const nextNews = Array.isArray(data?.data) ? data.data : [];

            if (!cancelled) {
              setNewsItems(nextNews);
              setSessionCache(NEWS_CACHE_KEY, nextNews);
            }
          })
          .catch((error) => {
            if (error.name !== 'AbortError') {
              console.error('Failed to build news search index:', error);
            }
          })
      );
    }

    if (!cachedCertificates.hasCache) {
      pendingRequests.push(
        fetchCollection(buildFreshUrl(API_URL.CERTIFICATES), controller.signal)
          .then((data) => {
            const nextCertificates = Array.isArray(data?.data) ? data.data : [];

            if (!cancelled) {
              setCertificates(nextCertificates);
              setSessionCache(CERTIFICATES_CACHE_KEY, nextCertificates);
            }
          })
          .catch((error) => {
            if (error.name !== 'AbortError') {
              console.error('Failed to build certificates search index:', error);
            }
          })
      );
    }

    if (!cachedCatalogs.hasCache) {
      pendingRequests.push(
        fetchCollection(buildFreshUrl(API_URL.CATALOGS), controller.signal)
          .then((data) => {
            const nextCatalogs = Array.isArray(data?.data) ? data.data : [];

            if (!cancelled) {
              setCatalogs(nextCatalogs);
              setSessionCache(CATALOGS_CACHE_KEY, nextCatalogs);
            }
          })
          .catch((error) => {
            if (error.name !== 'AbortError') {
              console.error('Failed to build catalog search index:', error);
            }
          })
      );
    }

    if (!needsFetch) {
      return () => {
        cancelled = true;
        controller.abort();
      };
    }

    Promise.allSettled(pendingRequests).finally(() => {
      if (!cancelled) {
        setIsSearchDataLoading(false);
      }
    });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [
    cachedCatalogs.hasCache,
    cachedCertificates.hasCache,
    cachedNews.hasCache,
    cachedProducts.hasCache,
    enabled,
    refreshToken,
  ]);

  useEffect(
    () =>
      subscribeToContentUpdates({
        keys: [
          CONTENT_SYNC_KEYS.PRODUCTS,
          CONTENT_SYNC_KEYS.NEWS,
          CONTENT_SYNC_KEYS.CERTIFICATES,
          CONTENT_SYNC_KEYS.CATALOGS,
        ],
        onUpdate: () => {
          removeSessionCache(PRODUCTS_CACHE_KEY);
          removeSessionCache(NEWS_CACHE_KEY);
          removeSessionCache(CERTIFICATES_CACHE_KEY);
          removeSessionCache(CATALOGS_CACHE_KEY);

          if (enabled) {
            setIsSearchDataLoading(true);
            setRefreshToken((current) => current + 1);
          }
        },
      }),
    [enabled]
  );

  return {
    searchDocuments: enabled
      ? buildSiteSearchDocuments({
          siteContent,
          products,
          newsItems,
          certificates,
          catalogs,
        })
      : [],
    isSearchIndexLoading: enabled
      ? (isSiteContentLoading && !siteContent) || isSearchDataLoading
      : false,
  };
}
